import Form from "@/components/Forms/Form";
import { FieldTypes } from "@/components/Forms/FieldTypes";
import { Box, Typography } from "@mui/material";
import { FormField } from "@/components/Forms/FormField";
import { useEffect, useState, useMemo } from "react";
import useApi from "@/hooks/useApi";
import Loading from "@/components/Widgets/Loading";

interface Medication {
  id: string;
  genericName: string;
  brandNames: string;
  class: string; // TODO TH: enumize this
}

const RegistrationMedications = (props: {
  onNext: (values: Record<string, unknown>) => void;
  /** Omitted: no Back button (the first health-history page after enrollment payment). */
  onBack?: (values: Record<string, unknown>) => void;
  /** The final page's button, when this is the last step. */
  submitButtonText?: string;
  values: Record<string, unknown>;
}) => {
  const api = useApi();
  const { onNext, onBack, values, submitButtonText } = props;

  const [medicationsCheckList, setMedicationsCheckList] = useState<
    Medication[]
  >([]);

  const getMedicationName = (medication: Medication) => {
    let name = medication.genericName;
    if (medication.brandNames.length) {
      name += " (" + medication.brandNames + ")";
    }
    return name;
  };

  const getSelectMedications = async () => {
    const result = await api.get<Medication[]>(`/data/medications/selected`);
    return result.data;
  };

  useEffect(() => {
    const fetchMedicationList = async () => {
      const medicationList = await getSelectMedications();
      setMedicationsCheckList(medicationList);
    };

    fetchMedicationList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pre-populate selectedMedicationsTaken with medications from currentMedications
  // that are also in the medicationsCheckList
  const initialSelectedMedications = useMemo(() => {
    type MedSelection = string | { id?: string };
    const existing = (values.selectedMedicationsTaken as string[]) || [];
    const currentMeds = (values.currentMedications as MedSelection[]) || [];

    // If there are no current medications to check, just return existing selections
    if (currentMeds.length === 0 || medicationsCheckList.length === 0) {
      return existing;
    }

    // Extract IDs from currentMedications (handle both string IDs and objects)
    const checklistIds = new Set(medicationsCheckList.map((med) => med.id));
    const currentMedIds = currentMeds
      .map((med) => (typeof med === "string" ? med : med.id || ""))
      .filter((id) => id && checklistIds.has(id));
    
    // Merge existing selections with matching current medications, removing duplicates
    const merged = [...new Set([...existing, ...currentMedIds])];
    return merged;
  }, [values.currentMedications, values.selectedMedicationsTaken, medicationsCheckList]);

  const getFormFields = (): FormField[] => {
    return [
      {
        id: "selectedMedicationsTaken",
        columns: 12,
        value: initialSelectedMedications,
        type: FieldTypes.MultipleChoice,
        choices: medicationsCheckList.map((medication: Medication) => {
          return {
            text: getMedicationName(medication),
            value: medication.id,
            ...medication,
          };
        }),
      },
    ];
  };

  if (medicationsCheckList.length === 0) {
    return <Loading />;
  }

  return (
    <Box>
      <Typography
        variant="h4"
        component="h1"
        sx={{
          color: "text.primary",
          fontWeight: 700,
          mb: 4,
        }}
      >
        Have you ever taken any of the following medications? (Check all that
        apply)
      </Typography>
      <Form
        fieldVariant="standard"
        fields={getFormFields()}
        onSubmit={onNext}
        submitButtonText={submitButtonText ?? "Next"}
        onCancel={onBack}
        cancelButtonText="Back"
      />
    </Box>
  );
};

export default RegistrationMedications;
